import React from 'react';
import { Link } from 'react-router-dom';
import { Apple, Play, Check, ArrowRight, Smartphone, ShieldCheck, Clock } from 'lucide-react';
import { brand } from '../mock';
import { useAuth } from '../context/AuthContext';
import { QrCodeCard, OpenInAppButton } from '../components/AppHandoff';
import { APP_STORE_URL, PLAY_STORE_URL } from '../lib/appLinks';

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function firstName(user) {
  if (!user) return '';
  if (user.name) return user.name.split(' ')[0];
  if (user.email) return user.email.split('@')[0];
  return '';
}

function TrialPill({ user }) {
  if (!user) return null;
  if (user.trial_active) {
    const daysLeft = daysUntil(user.trial_expires_at);
    return (
      <span data-testid="download-trial-pill-active" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-gold/20 text-brand-gold text-xs font-medium border border-brand-gold/40">
        <Clock className="w-3 h-3" /> Trial active · {daysLeft} day{daysLeft === 1 ? '' : 's'} left
      </span>
    );
  }
  if (user.trial_started_at) {
    return (
      <span data-testid="download-trial-pill-expired" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-cream/15 text-brand-cream/85 text-xs font-medium border border-brand-cream/25">
        Trial ended
      </span>
    );
  }
  return (
    <span data-testid="download-trial-pill-pending" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-cream/15 text-brand-cream/85 text-xs font-medium border border-brand-cream/25">
      Ready to activate
    </span>
  );
}

const TRIAL_STEPS = [
  { icon: Smartphone, title: 'Download the app', desc: 'Grab LISTING AiGENT on iOS or Android — free to install.' },
  { icon: ShieldCheck, title: 'Activate for $10', desc: '$10 unlocks full access for 7 days, then $100/month until you cancel.' },
  { icon: Clock, title: 'Use it for 7 days', desc: 'Full functionality for a full week. Upgrade only if it earns its keep.' },
];

const INCLUDED = [
  'Unlimited AI call answering during trial',
  'Import listings from Zillow, Redfin, Realtor',
  'Auto-scheduling into your calendar',
  'Full call transcripts & summaries',
  'Cancel before day 7 — no monthly charge',
];

function StoreButton({ icon: Icon, storeLabel, appName, href, testId }) {
  const inner = (
    <>
      <Icon className="w-8 h-8 shrink-0" strokeWidth={1.8} />
      <div className="text-left leading-tight">
        <div className="text-[10px] uppercase tracking-widest text-brand-cream/70">{storeLabel}</div>
        <div className="font-serif text-xl">{appName}</div>
      </div>
      {!href && (
        <span className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-brand-gold text-brand-green-darker">
          Coming Soon
        </span>
      )}
    </>
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className="group relative flex items-center gap-4 w-full sm:w-72 px-5 py-4 rounded-xl bg-brand-green text-brand-cream border border-brand-green shadow-lg hover:bg-brand-green-dark transition-colors"
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      disabled
      aria-disabled="true"
      data-testid={testId}
      className="group relative flex items-center gap-4 w-full sm:w-72 px-5 py-4 rounded-xl bg-brand-green text-brand-cream border border-brand-green shadow-lg opacity-95 cursor-not-allowed"
    >
      {inner}
    </button>
  );
}

function DownloadHero() {
  const { user } = useAuth();
  const name = firstName(user);
  return (
    <section className="relative overflow-hidden bg-brand-green text-brand-cream py-20 md:py-28">
      <div className="absolute inset-0 bg-gradient-to-br from-brand-green via-brand-green-dark to-brand-green-darker opacity-80" />
      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <img
          src="/logo512.png"
          alt="LISTING AiGENT logo"
          width="96"
          height="96"
          className="w-24 h-24 mx-auto rounded-full object-cover shadow-2xl mb-8"
        />
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Downloads</span>
        <h1 data-testid="download-hero-title" className="mt-4 font-serif text-5xl md:text-6xl leading-tight">
          {user
            ? <>Ready when you are, <span className="text-brand-gold">{name}</span>&nbsp;&mdash; install below.</>
            : <>Download LISTING AiGENT<br className="hidden md:block" /> and start your trial.</>
          }
        </h1>
        <p className="mt-6 text-lg text-brand-cream/80 max-w-2xl mx-auto">
          {user
            ? 'Install the app on your iPhone or Android. Scan the code below and the app opens already signed in.'
            : 'Install the app on your iPhone or Android to start your 7-day trial.'
          }
        </p>

        {user && (
          <div className="mt-6 flex justify-center">
            <TrialPill user={user} />
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <StoreButton
            icon={Apple}
            storeLabel="Download on the"
            appName="App Store"
            href={APP_STORE_URL}
            testId="download-appstore-btn"
          />
          <StoreButton
            icon={Play}
            storeLabel="Get it on"
            appName="Google Play"
            href={PLAY_STORE_URL}
            testId="download-playstore-btn"
          />
        </div>
        <OpenInAppButton />
        <QrCodeCard />
        <p className="mt-6 text-xs text-brand-cream/60">
          Live on the App Store and Google Play — install, sign in with the same email, and your account picks up right where you left off.
        </p>
      </div>
    </section>
  );
}

function HowTrialWorks() {
  return (
    <section className="py-24">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-green/60">Get started in minutes</span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-green">How the trial works</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TRIAL_STEPS.map((s) => (
            <div
              key={s.title}
              className="p-8 rounded-2xl bg-white border border-brand-cream-dark hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-green flex items-center justify-center mb-6">
                <s.icon className="w-5 h-5 text-brand-cream" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-2xl text-brand-green mb-3">{s.title}</h3>
              <p className="text-brand-green/70 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatsIncluded() {
  return (
    <section className="py-20 bg-brand-cream-dark/40">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="bg-white rounded-3xl border border-brand-cream-dark p-10 md:p-12">
          <span className="text-xs uppercase tracking-[0.2em] text-brand-green/60">What&apos;s included</span>
          <h2 className="mt-3 font-serif text-3xl md:text-4xl text-brand-green">
            Your 7-day trial gives you full access.
          </h2>
          <ul className="mt-8 space-y-3">
            {INCLUDED.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-brand-green mt-0.5 shrink-0" />
                <span className="text-brand-green/80">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 pt-6 border-t border-brand-cream-dark flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-brand-green/70">
              After 7 days your plan automatically renews at <strong>$100 / month</strong> until you cancel. Cancel anytime before the trial ends and you won&apos;t be charged again.
            </p>
            <Link
              to="/pricing"
              className="shrink-0 inline-flex items-center gap-2 text-brand-green font-semibold border-b border-brand-green pb-0.5 hover:gap-3 transition-all"
            >
              See pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactFallback() {
  return (
    <section className="py-20">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <h3 className="font-serif text-2xl md:text-3xl text-brand-green">Need early access?</h3>
        <p className="mt-3 text-brand-green/70">
          Reach out and we&apos;ll let you know the moment LISTING AiGENT is live in your store.
        </p>
        <a
          href={`mailto:${brand.email}?subject=Early%20access%20for%20LISTING%20AiGENT`}
          className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.02] transition-transform"
        >
          Email us for early access <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
}

export default function Download() {
  return (
    <div className="bg-brand-cream">
      <DownloadHero />
      <HowTrialWorks />
      <WhatsIncluded />
      <ContactFallback />
    </div>
  );
}
